import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import userService from '../../services/userService';
import '../../styles/modal.css';

/**
 * Componente modal reutilizable para editar usuarios
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Indica si el modal está abierto
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {Object} props.user - Usuario a editar
 * @param {Function} props.onUserUpdated - Función que se ejecuta cuando el usuario se actualiza
 * @param {boolean} props.isNewUser - Indica si es un nuevo usuario (para crear en lugar de editar)
 */
const UserEditModal = ({ isOpen, onClose, user, onUserUpdated, isNewUser = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    phone: '',
    status: 'active',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Cargar datos del usuario cuando se abre el modal
  useEffect(() => {
    if (isOpen && user && !isNewUser) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // No mostrar la contraseña actual
        role: user.role || 'user',
        phone: user.phone || '',
        status: user.status || 'active',
      });
    } else if (isOpen && isNewUser) {
      // Resetear el formulario para un nuevo usuario
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        phone: '',
        status: 'active',
      });
    }
  }, [isOpen, user, isNewUser]);

  // Manejar cambios en los campos del formulario
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  }, []);

  // Manejar envío del formulario
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const userData = { ...formData };

        // Solo incluir la contraseña si se ha proporcionado una nueva
        if (!userData.password) {
          delete userData.password;
        }

        let response;

        if (isNewUser) {
          // Crear nuevo usuario
          response = await userService.createUser(userData);
        } else {
          // Actualizar usuario existente
          response = await userService.updateUser(user._id, userData);
        }

        const updatedUser = (response.data && response.data.user) || response.data;

        setSuccess(true);

        // Notificar al componente padre
        if (onUserUpdated) {
          onUserUpdated(updatedUser, isNewUser);
        }

        // Cerrar el modal después de un breve retraso para mostrar el mensaje de éxito
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error) {
        console.error('Error saving user:', error);
        setError('Error al guardar el usuario: ' + (error.message || 'Error desconocido'));
      } finally {
        setLoading(false);
      }
    },
    [formData, isNewUser, user, onUserUpdated, onClose],
  );

  // Si el modal no está abierto, no renderizar nada
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{isNewUser ? 'Añadir Usuario' : 'Editar Usuario'}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="modal-body">
          {success && (
            <div className="alert alert-success">
              Usuario {isNewUser ? 'creado' : 'actualizado'} correctamente
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Nombre</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">
                Contraseña {!isNewUser && '(dejar en blanco para mantener la actual)'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="form-control"
                required={isNewUser}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="role">Rol</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="user">Usuario</option>
                  <option value="parent">Padre/Madre</option>
                  <option value="coach">Entrenador</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="status">Estado</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="active">Activo</option>
                  <option value="pending">Pendiente</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="phone">Teléfono</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// PropTypes para validación de props
UserEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    role: PropTypes.oneOf(['user', 'parent', 'coach', 'admin']),
    phone: PropTypes.string,
    status: PropTypes.oneOf(['active', 'pending', 'inactive']),
    wallet: PropTypes.number,
    walletBalance: PropTypes.number,
    xpPoints: PropTypes.number,
    avatar: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    lastLogin: PropTypes.string,
    emailVerified: PropTypes.bool
  }),
  onUserUpdated: PropTypes.func.isRequired,
  isNewUser: PropTypes.bool
};

export default UserEditModal;
